/**
 * Dexpert HRMS role slugs — match backend roles.name (configurable master data).
 * Dashboard tiers: admin | checker | maker | user
 */

export const ROLES = {
  SUPER_ADMINISTRATOR: 'super_administrator',
  PENSIONER: 'pensioner',
};

export const DASHBOARDS = {
  ADMIN: 'admin',
  CHECKER: 'checker',
  MAKER: 'maker',
  PENSIONER: 'pensioner',
  EMPLOYEE: 'employee',
};

export const DASHBOARD_ROUTES = {
  [DASHBOARDS.ADMIN]: '/admin-dashboard',
  [DASHBOARDS.CHECKER]: '/checker-dashboard',
  [DASHBOARDS.MAKER]: '/maker-dashboard',
  [DASHBOARDS.PENSIONER]: '/pensioner-dashboard',
  [DASHBOARDS.EMPLOYEE]: '/user-dashboard',
};

export const DASHBOARD_DISPLAY_NAMES = {
  [DASHBOARDS.ADMIN]: 'Admin Dashboard',
  [DASHBOARDS.CHECKER]: 'Checker Dashboard',
  [DASHBOARDS.MAKER]: 'Maker Dashboard',
  [DASHBOARDS.PENSIONER]: 'Pensioner Dashboard',
  [DASHBOARDS.EMPLOYEE]: 'Employee Dashboard',
};

export const ROLE_DISPLAY_NAMES = {
  [ROLES.SUPER_ADMINISTRATOR]: 'Super Administrator',
  [ROLES.PENSIONER]: 'Pensioner',
};

/** Role slug → dashboard tier (fallback when API does not send dashboard_tier) */
export const ROLE_DASHBOARD_MAP = {
  [ROLES.SUPER_ADMINISTRATOR]: DASHBOARDS.ADMIN,
  [ROLES.PENSIONER]: DASHBOARDS.PENSIONER,
};

export const ADMIN_DASHBOARD_ROLES = [
  ROLES.SUPER_ADMINISTRATOR,
  'admin',
  'super_admin',
];

export const PRIVILEGED_ADMIN_ROLES = [
  ROLES.CHAIRMAN,
  ROLES.HOSPITAL_DIRECTOR,
  ROLES.SUPER_ADMINISTRATOR,
  ROLES.HOSPITAL_OWNER,
  'admin',
];

const LEGACY_ALIASES = {
  admin: ROLES.SUPER_ADMINISTRATOR,
  super_admin: ROLES.HOSPITAL_OWNER,
  hr_officer: ROLES.HR_EXECUTIVE,
  department_head: ROLES.DEPARTMENT_HEAD,
  dept_head: ROLES.MEDICAL_DEPT_HEAD,
  maker: ROLES.PAYROLL_OPERATOR,
  checker: ROLES.FINANCE_APPROVER,
  accountant: ROLES.FINANCE_MANAGER,
  employee: ROLES.STAFF,
  readonly: ROLES.AUDITOR,
  accounts_manager: ROLES.FINANCE_MANAGER,
  office_head: ROLES.MEDICAL_DEPT_HEAD,
  finance_head: ROLES.FINANCE_APPROVER,
  senior_employee: ROLES.STAFF,
  pensioner: 'pensioner',
};

export function normalizeRoleSlug(roleName) {
  if (!roleName) return null;
  if (ROLE_DISPLAY_NAMES[roleName]) return roleName;
  return LEGACY_ALIASES[roleName] || roleName;
}

export function getRoleDisplayName(roleName) {
  const slug = normalizeRoleSlug(roleName);
  return ROLE_DISPLAY_NAMES[slug] || roleName || 'Staff';
}

export function roleMatches(roleName, allowedRoles) {
  if (!roleName) return false;
  const normalized = normalizeRoleSlug(roleName);
  const allowed = Array.isArray(allowedRoles) ? allowedRoles : [allowedRoles];
  const normalizedAllowed = new Set(allowed.map((r) => normalizeRoleSlug(r) || r));
  return normalizedAllowed.has(normalized) || normalizedAllowed.has(roleName);
}

export function getRoleDashboard(roleName) {
  const slug = normalizeRoleSlug(roleName);
  return ROLE_DASHBOARD_MAP[slug] || DASHBOARDS.EMPLOYEE;
}

/** Resolve post-login path from user object (prefers API dashboard_route) */
export const getDashboardPath = (user) => {
  if (!user) return '/login';

  // Check for pensioner role specifically
  if (user.role_name === 'pensioner' || user.role_name === 'Pensioner') {
    return '/pensioner-dashboard';
  }

  // Use dashboard_tier from backend if available, otherwise default to 'user'
  const tier = user.dashboard_tier || 'user';

  const DASHBOARD_ROUTES = {
    'admin': '/admin-dashboard',
    'checker': '/checker-dashboard',
    'maker': '/maker-dashboard',
    'user': '/user-dashboard',
  };

  return DASHBOARD_ROUTES[tier] || '/user-dashboard';
};

export function hasFullAccess(user) {
  if (!user) return false;
  if (user.permissions?.includes('*')) return true;
  if (user.dashboard_tier === 'admin' && user.visibility_scope === 'organization') return true;
  return false;
}

/** Roles offered in employee creation — loaded from API when available */
export const EMPLOYEE_CREATION_ROLE_OPTIONS = Object.entries(ROLE_DISPLAY_NAMES).map(([role, label]) => ({
  value: role,
  label,
  dashboard: ROLE_DASHBOARD_MAP[role] || DASHBOARDS.EMPLOYEE,
  dashboardLabel: DASHBOARD_DISPLAY_NAMES[ROLE_DASHBOARD_MAP[role] || DASHBOARDS.EMPLOYEE],
}));
